import { useCallback, useEffect, useRef, useState } from "react";
import { PaymentService } from "../services";
import { usePersistStore, useStore } from "../store";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../controls";

// How long we'll wait for Trustap's webhook to flip the payment to "paid"
// before telling the user to come back later.
const POLL_INTERVAL_MS = 15000;
const POLL_TIMEOUT_MS = 300000;

// Terminal failure statuses (post-strip of the "basic_tx." prefix). When
// Trustap lands in one of these, polling should stop and the user should
// see a clear failure message rather than the pending spinner. Per docs:
// https://docs.trustap.com/docs/concepts/webhooks#webhook-event-codes
const FAILED_STATUSES = new Set([
  "cancelled",
  "payment_failed",
  "funds_refunded",
]);

const humanizeStatus = (raw: string) => {
  if (!raw) return "";
  return raw
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

function PaymentCallback() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tx_id = params.get("tx_id") || "";
  const navigate = useNavigate();

  const [isDone, setIsDone] = useState(false);
  const [status, setStatus] = useState("");
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
  const [didTimeOut, setDidTimeOut] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const pollStartRef = useRef<number>(Date.now());
  const statusRef = useRef<string>("");

  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage } = useStore((s: any) => s);

  const reload = useCallback(async () => {
    if (!token || !tx_id) return;
    setIsReloading(true);
    try {
      const result = await PaymentService.getStatus(token, {
        paymentId: tx_id,
      });

      if (result?.error) {
        setErrorMessage(result?.error);
        setIsDone(true);
        return;
      }

      const next = result?.data?.status || "";
      statusRef.current = next;
      setStatus(next);
      setIsPaymentInProgress(!!result?.data?.isPaymentInProgress);
      setIsDone(true);
    } finally {
      setIsReloading(false);
    }
  }, [token, tx_id, setErrorMessage]);

  useEffect(() => {
    if (!tx_id) {
      setErrorMessage(
        "Payment ID not found in the URL. If you just paid, please check your email for confirmation."
      );
      setIsDone(true);
    }
  }, [tx_id, setErrorMessage]);

  // Poll until status reaches a terminal state (paid or failed) or we hit
  // the timeout. The webhook that writes status to our DB arrives
  // asynchronously, so arriving on this page doesn't mean the DB has caught
  // up yet. `statusRef` avoids the stale-closure trap inside setInterval.
  useEffect(() => {
    if (!token || !tx_id) return;
    reload();
    pollStartRef.current = Date.now();

    const interval = setInterval(() => {
      if (
        statusRef.current === "paid" ||
        FAILED_STATUSES.has(statusRef.current)
      ) {
        clearInterval(interval);
        return;
      }
      if (Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) {
        clearInterval(interval);
        setDidTimeOut(true);
        return;
      }
      reload();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tx_id]);

  const isPaid = status === "paid";
  const isFailed = FAILED_STATUSES.has(status);
  const isPending = isDone && !isPaid && !isFailed && !didTimeOut;
  const isLoading = !isDone;

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div
            className="paymentCallback__card p-5 text-center"
            style={{ minHeight: 360 }}
          >
            {isLoading && (
              <>
                <div
                  className="spinner-border text-primary mb-4"
                  style={{ width: 64, height: 64 }}
                  role="status"
                  aria-hidden="true"
                />
                <h4 className="mb-2">Checking your payment…</h4>
                <p className="text-muted m-0">
                  Hang tight while we confirm with Trustap.
                </p>
              </>
            )}

            {isPaid && (
              <>
                <i
                  className="bi bi-check2-circle text-success"
                  style={{ fontSize: 96, lineHeight: 1 }}
                  aria-hidden="true"
                />
                <h3 className="mt-3 mb-2 text-success">Payment confirmed</h3>
                <p className="text-muted mb-4">
                  Thank you! Your payment has been received. The seller will
                  arrange shipping shortly.
                </p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <Button
                    title="View My Bids"
                    onClick={() => navigate("/dashboard/bids")}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/")}
                  >
                    Back to home
                  </button>
                </div>
              </>
            )}

            {isFailed && (
              <>
                <i
                  className="bi bi-x-circle text-danger"
                  style={{ fontSize: 96, lineHeight: 1 }}
                  aria-hidden="true"
                />
                <h3 className="mt-3 mb-2 text-danger">Payment not completed</h3>
                <p className="text-muted mb-1">
                  Status:{" "}
                  <span className="fw-semibold">{humanizeStatus(status)}</span>
                </p>
                <p className="text-muted mb-4">
                  Trustap has closed this transaction without collecting
                  payment. You can try again from your bids page.
                </p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <Button
                    title="Go to My Bids"
                    onClick={() => navigate("/dashboard/bids")}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/")}
                  >
                    Back to home
                  </button>
                </div>
              </>
            )}

            {isPending && (
              <>
                <i
                  className="bi bi-hourglass-split text-warning"
                  style={{ fontSize: 96, lineHeight: 1 }}
                  aria-hidden="true"
                />
                <h4 className="mt-3 mb-2">Payment pending</h4>
                <p className="text-muted mb-1">
                  Current status:{" "}
                  <span className="fw-semibold">{humanizeStatus(status)}</span>
                </p>
                <p className="text-muted mb-4">
                  {isPaymentInProgress
                    ? "Your payment is being verified by Trustap. This usually only takes a moment — we'll email you as soon as it clears."
                    : "We're still waiting for confirmation from Trustap."}
                </p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <Button
                    title={isReloading ? "Refreshing…" : "Refresh"}
                    svg={
                      isReloading ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : null
                    }
                    onClick={reload}
                    disabled={isReloading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/")}
                  >
                    Back to home
                  </button>
                </div>
              </>
            )}

            {didTimeOut && !isPaid && (
              <>
                <i
                  className="bi bi-clock-history text-muted"
                  style={{ fontSize: 96, lineHeight: 1 }}
                  aria-hidden="true"
                />
                <h4 className="mt-3 mb-2">Still processing</h4>
                <p className="text-muted mb-4">
                  Your payment is still being processed. This can take a few
                  minutes. You can safely close this page — we'll email you as
                  soon as it's confirmed.
                </p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <Button
                    title={isReloading ? "Checking…" : "Check again"}
                    svg={
                      isReloading ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : null
                    }
                    onClick={reload}
                    disabled={isReloading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/")}
                  >
                    Back to home
                  </button>
                </div>
              </>
            )}

            {tx_id && (
              <p
                className="text-muted small mt-4 mb-0"
                style={{ fontSize: "0.75rem" }}
              >
                Payment ID: {tx_id}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentCallback;
